import React, { useMemo, useState, useCallback } from 'react';
import {
  MapPin, Phone, Search, Star, Plus, Pencil, Trash2,
  X, Loader2, AlertCircle, Building2, Check,
} from 'lucide-react';
import { useMyAnagrafica, DestinationDiv } from '../../hooks/useMyAnagrafica';
import api from '../../services/api';
import { toast } from 'sonner';
import { ProvinciaSelect } from '../../components/form/ProvinciaSelect';

// ── Form vuoto ────────────────────────────────────────────────────────────────
const EMPTY_FORM = {
  dd_nomdest: '',
  dd_inddest: '',
  dd_capdest: '',
  dd_locdest: '',
  dd_prodest: '',
  dd_telef:   '',
  dd_email:   '',
};

type DestForm = typeof EMPTY_FORM;

// ── Modale add/edit ───────────────────────────────────────────────────────────
function DestModal({
  editing,
  onClose,
  onSaved,
}: {
  editing: DestinationDiv | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const isEdit = editing !== null;
  const [form, setForm] = useState<DestForm>(() =>
    isEdit
      ? {
          dd_nomdest: editing.dd_nomdest ?? '',
          dd_inddest: editing.inddest    ?? '',
          dd_capdest: editing.cap        ?? '',
          dd_locdest: editing.citta      ?? '',
          dd_prodest: editing.prov       ?? '',
          dd_telef:   editing.telef      ?? '',
          dd_email:   editing.email      ?? '',
        }
      : { ...EMPTY_FORM }
  );
  const [saving, setSaving] = useState(false);

  const set = (k: keyof DestForm) => (e: React.ChangeEvent<HTMLInputElement>) =>
    setForm(f => ({ ...f, [k]: e.target.value }));

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.dd_nomdest.trim()) { toast.error('Il nome destinazione è obbligatorio.'); return; }
    setSaving(true);
    try {
      if (isEdit) {
        await api.put(`/api/clienti/me/destinazioni/${editing!.dd_coddest}`, form);
        toast.success('Destinazione aggiornata.');
      } else {
        await api.post('/api/clienti/me/destinazioni', form);
        toast.success('Destinazione aggiunta.');
      }
      onSaved();
      onClose();
    } catch (err: any) {
      const msg = err?.response?.data?.detail ?? 'Errore nel salvataggio. Riprova.';
      toast.error(typeof msg === 'string' ? msg : 'Errore nel salvataggio.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      onClick={e => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="absolute inset-0 bg-black/40 backdrop-blur-[2px]" />
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-lg animate-in fade-in zoom-in-95 duration-200">
        {/* Header */}
        <div className="flex items-center justify-between px-6 pt-5 pb-4 border-b">
          <div>
            <h2 className="text-base font-semibold">
              {isEdit ? 'Modifica destinazione' : 'Nuova destinazione'}
            </h2>
            <p className="text-xs text-muted-foreground mt-0.5">
              Campi corrispondenti al gestionale NTS
            </p>
          </div>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground p-1 rounded-lg hover:bg-secondary/60 transition-colors">
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {/* Nome destinazione */}
          <div>
            <label className="block text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-1.5">
              Nome / descrizione <span className="text-red-500">*</span>
            </label>
            <input
              value={form.dd_nomdest}
              onChange={set('dd_nomdest')}
              placeholder="Es. Magazzino Nord, Sede operativa…"
              className="w-full h-9 px-3 rounded-lg border text-sm focus:outline-none focus:ring-2 focus:ring-[hsl(214,58%,41%)]/30"
              data-testid="dest-field-nome"
              required
            />
          </div>

          {/* Indirizzo */}
          <div>
            <label className="block text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-1.5">
              Indirizzo
            </label>
            <input
              value={form.dd_inddest}
              onChange={set('dd_inddest')}
              placeholder="Via/Piazza e numero civico"
              className="w-full h-9 px-3 rounded-lg border text-sm focus:outline-none focus:ring-2 focus:ring-[hsl(214,58%,41%)]/30"
              data-testid="dest-field-indirizzo"
            />
          </div>

          {/* CAP / Città / Prov */}
          <div className="grid grid-cols-5 gap-2">
            <div className="col-span-1">
              <label className="block text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-1.5">CAP</label>
              <input
                value={form.dd_capdest}
                onChange={set('dd_capdest')}
                placeholder="00100"
                maxLength={10}
                className="w-full h-9 px-3 rounded-lg border text-sm focus:outline-none focus:ring-2 focus:ring-[hsl(214,58%,41%)]/30"
                data-testid="dest-field-cap"
              />
            </div>
            <div className="col-span-2">
              <label className="block text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-1.5">Città</label>
              <input
                value={form.dd_locdest}
                onChange={set('dd_locdest')}
                placeholder="Roma"
                className="w-full h-9 px-3 rounded-lg border text-sm focus:outline-none focus:ring-2 focus:ring-[hsl(214,58%,41%)]/30"
                data-testid="dest-field-citta"
              />
            </div>
            <div className="col-span-2">
              <label className="block text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-1.5">Prov.</label>
              <ProvinciaSelect
                value={form.dd_prodest}
                onChange={v => setForm(f => ({ ...f, dd_prodest: v }))}
                size="sm"
                testid="dest-field-prov"
              />
            </div>
          </div>

          {/* Telefono / Email */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-1.5">Telefono</label>
              <input
                value={form.dd_telef}
                onChange={set('dd_telef')}
                type="tel"
                placeholder="+39 06 000000"
                className="w-full h-9 px-3 rounded-lg border text-sm focus:outline-none focus:ring-2 focus:ring-[hsl(214,58%,41%)]/30"
                data-testid="dest-field-telef"
              />
            </div>
            <div>
              <label className="block text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-1.5">Email</label>
              <input
                value={form.dd_email}
                onChange={set('dd_email')}
                type="email"
                placeholder="spedizioni@azienda.it"
                className="w-full h-9 px-3 rounded-lg border text-sm focus:outline-none focus:ring-2 focus:ring-[hsl(214,58%,41%)]/30"
                data-testid="dest-field-email"
              />
            </div>
          </div>

          {/* Azioni */}
          <div className="flex gap-2.5 pt-2">
            <button type="button" onClick={onClose}
              className="flex-1 h-10 rounded-xl border text-sm font-medium hover:bg-secondary/60 transition-colors">
              Annulla
            </button>
            <button type="submit" disabled={saving}
              className="flex-1 h-10 rounded-xl bg-[hsl(214,58%,41%)] text-white text-sm font-semibold flex items-center justify-center gap-2 hover:bg-[hsl(214,58%,46%)] transition-colors disabled:opacity-60"
              data-testid="dest-save-btn">
              {saving
                ? <><Loader2 className="w-4 h-4 animate-spin" /> Salvataggio…</>
                : <><Check className="w-4 h-4" /> {isEdit ? 'Salva modifiche' : 'Aggiungi'}</>
              }
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ── Pagina principale ─────────────────────────────────────────────────────────
export default function IndirizziSpedizionePage() {
  const { data: ana, loading, error, reload } = useMyAnagrafica();
  const [search, setSearch] = useState('');
  const [modal, setModal] = useState<'add' | DestinationDiv | null>(null);
  const [deletingCode, setDeletingCode] = useState<number | null>(null);

  const destinazioni = useMemo(() => ana?.destinazioni ?? [], [ana]);
  // La predefinita è `an_destin` del Gestionale; 0 (o nessuna fra quelle
  // mostrate) vuol dire la sede legale.
  const destPrefNts = ana?.destinazionePreferita ?? 0;
  const destPref = destinazioni.some(d => d.dd_coddest === destPrefNts) ? destPrefNts : null;
  const sedePredefinita = destPref == null;

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    // Ordina: predefinita sempre in cima
    const sorted = [...destinazioni].sort((a, b) => {
      const aDefault = destPref != null ? a.dd_coddest === destPref : false;
      const bDefault = destPref != null ? b.dd_coddest === destPref : false;
      if (aDefault && !bDefault) return -1;
      if (!aDefault && bDefault) return 1;
      return 0;
    });
    if (!q) return sorted;
    return sorted.filter(d =>
      [d.dd_nomdest, d.inddest, d.citta, d.prov, d.cap]
        .filter(Boolean)
        .some(v => (v as string).toLowerCase().includes(q))
    );
  }, [destinazioni, search, destPref]);

  const handleDelete = useCallback(async (code: number) => {
    setDeletingCode(code);
    try {
      await api.delete(`/api/clienti/me/destinazioni/${code}`);
      toast.success('Destinazione eliminata.');
      reload?.();
    } catch (err: any) {
      const msg = err?.response?.data?.detail ?? 'Errore durante l\'eliminazione.';
      toast.error(typeof msg === 'string' ? msg : 'Errore durante l\'eliminazione.');
    } finally {
      setDeletingCode(null);
    }
  }, [reload]);

  const handleSetPredefinita = useCallback(async (code: number) => {
    try {
      await api.put(`/api/clienti/me/destinazioni/${code}/predefinita`);
      toast.success(code === 0 ? 'La sede legale è ora l\'indirizzo predefinito.' : 'Destinazione impostata come predefinita.');
      reload?.();
    } catch (err: any) {
      const msg = err?.response?.data?.detail ?? 'Errore durante l\'aggiornamento.';
      toast.error(typeof msg === 'string' ? msg : 'Errore durante l\'aggiornamento.');
    }
  }, [reload]);

  return (
    <div data-testid="indirizzi-spedizione-page" className="space-y-5">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Indirizzi di spedizione</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Gestisci le destinazioni alternative associate al tuo codice cliente.
          </p>
        </div>
        <button
          onClick={() => setModal('add')}
          className="h-9 px-4 rounded-xl bg-[hsl(214,58%,41%)] text-white text-sm font-medium flex items-center gap-2 hover:bg-[hsl(214,58%,46%)] transition-colors flex-shrink-0"
          data-testid="dest-add-btn"
        >
          <Plus className="w-4 h-4" /> Aggiungi destinazione
        </button>
      </div>

      {error && (
        <div className="rounded-lg border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-800 flex items-center gap-2">
          <AlertCircle className="w-4 h-4" /> {error}
        </div>
      )}

      {/* Sede legale */}
      <div className="rounded-xl border bg-white p-5">
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-base font-semibold">Sede legale</h2>
          {sedePredefinita ? (
            <span className="inline-flex items-center gap-1 text-[10px] font-semibold text-emerald-700 bg-emerald-50 px-2 py-0.5 rounded-full border border-emerald-200" data-testid="sede-predefinita">
              <Star className="w-2.5 h-2.5 fill-current" /> Predefinita
            </span>
          ) : (
            <button
              onClick={() => handleSetPredefinita(0)}
              className="h-7 px-2.5 rounded-lg border text-xs font-medium flex items-center gap-1 text-muted-foreground hover:text-amber-600 hover:border-amber-300 hover:bg-amber-50 transition-colors"
              data-testid="sede-set-default"
            >
              <Star className="w-3.5 h-3.5" /> Rendi predefinita
            </button>
          )}
        </div>
        <AddressRow
          icon={<Building2 className="w-4 h-4" />}
          name={ana?.ragioneSociale}
          line1={ana?.indirizzo}
          city={[ana?.cap, ana?.citta, ana?.provincia].filter(Boolean).join(' ')}
          country={ana?.paese || 'Italia'}
          phone={ana?.telefono}
        />
      </div>

      {/* Lista destinazioni */}
      <div className="rounded-xl border bg-white p-5">
        <div className="flex items-center justify-between gap-3 mb-4 flex-wrap">
          <h2 className="text-base font-semibold">
            Destinazioni alternative
            <span className="text-sm text-muted-foreground font-normal ml-1.5">({destinazioni.length})</span>
          </h2>
          {destinazioni.length > 4 && (
            <div className="relative">
              <Search className="absolute left-2.5 top-2.5 w-4 h-4 text-muted-foreground" />
              <input
                data-testid="dest-search"
                value={search}
                onChange={e => setSearch(e.target.value)}
                placeholder="Cerca…"
                className="h-9 w-56 pl-8 pr-3 rounded-lg border bg-white text-sm focus:outline-none focus:ring-2 focus:ring-[hsl(210,64%,29%)]/30"
              />
            </div>
          )}
        </div>

        {loading ? (
          <div className="space-y-3">
            {Array.from({ length: 3 }).map((_, i) => (
              <div key={i} className="h-24 bg-[hsl(0,0%,96%)] rounded-lg animate-pulse" />
            ))}
          </div>
        ) : destinazioni.length === 0 ? (
          <div className="text-center py-10 border border-dashed rounded-lg" data-testid="dest-empty">
            <MapPin className="w-10 h-10 text-muted-foreground mx-auto mb-3" />
            <h3 className="text-sm font-semibold">Nessuna destinazione alternativa</h3>
            <p className="text-xs text-muted-foreground mt-1 mb-4">Le spedizioni verranno consegnate alla sede legale.</p>
            <button onClick={() => setModal('add')}
              className="h-8 px-4 rounded-lg bg-[hsl(214,58%,41%)] text-white text-xs font-medium hover:bg-[hsl(214,58%,46%)] transition-colors">
              + Aggiungi la prima
            </button>
          </div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-8 text-sm text-muted-foreground">Nessun risultato.</div>
        ) : (
          <ul className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="dest-list">
            {filtered.map((d, idx) => {
              const isDefault = d.dd_coddest === destPref;
              const deleting = deletingCode === d.dd_coddest;
              return (
                <li
                  key={`${d.dd_coddest}-${idx}`}
                  data-testid={`dest-item-${d.dd_coddest}`}
                  className={`rounded-xl border p-4 transition-colors ${isDefault ? 'border-[hsl(210,64%,29%)] bg-[hsl(210,40%,98%)]' : 'hover:bg-secondary/20'}`}
                >
                  {/* Fino a xl le schede (due colonne) sono strette: le azioni sotto l'indirizzo, che altrimenti va a
                      capo a ogni parola accanto a "Rendi predefinita". */}
                  <div className="flex flex-col gap-3 xl:flex-row xl:items-start xl:justify-between xl:gap-2">
                    {/* Info indirizzo */}
                    <div className="flex items-start gap-2.5 min-w-0 flex-1">
                      <MapPin className="w-4 h-4 text-[hsl(210,64%,29%)] mt-0.5 flex-none" />
                      <div className="min-w-0">
                        <div className="font-semibold text-sm">{d.dd_nomdest || `Destinazione ${d.dd_coddest}`}</div>
                        {d.inddest && <div className="text-xs text-muted-foreground">{d.inddest}</div>}
                        <div className="text-xs text-muted-foreground">
                          {[d.cap, d.citta, d.prov].filter(Boolean).join(' · ')}
                        </div>
                        {d.telef && (
                          <div className="text-xs text-muted-foreground flex items-center gap-1 mt-0.5">
                            <Phone className="w-3 h-3" /> {d.telef}
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Badge + azioni */}
                    <div className="flex items-center justify-between gap-2 flex-none xl:flex-col xl:items-end">
                      <div className="flex items-center gap-1.5">
                        <span className="text-xs font-bold px-2 py-0.5 rounded bg-secondary/60 text-muted-foreground">
                          #{d.dd_coddest}
                        </span>
                        {isDefault && (
                          <span className="inline-flex items-center gap-1 text-[10px] font-semibold text-emerald-700 bg-emerald-50 px-2 py-0.5 rounded-full border border-emerald-200">
                            <Star className="w-2.5 h-2.5 fill-current" /> Predefinita
                          </span>
                        )}
                      </div>
                      {/* Pulsanti azione */}
                      <div className="flex items-center gap-1">
                        {/* Con la scritta, come sulla sede legale: la sola
                            stella non si capiva (cliente, 02/11/2026). */}
                        {!isDefault && (
                          <button
                            onClick={() => handleSetPredefinita(d.dd_coddest!)}
                            className="h-7 px-2.5 rounded-lg border text-xs font-medium flex items-center gap-1 whitespace-nowrap text-muted-foreground hover:text-amber-600 hover:border-amber-300 hover:bg-amber-50 transition-colors"
                            data-testid={`dest-set-default-${d.dd_coddest}`}
                          >
                            <Star className="w-3.5 h-3.5" /> Rendi predefinita
                          </button>
                        )}
                        <button
                          onClick={() => setModal(d)}
                          title="Modifica"
                          className="w-7 h-7 rounded-lg border flex items-center justify-center text-muted-foreground hover:text-[hsl(214,58%,41%)] hover:border-[hsl(214,58%,41%)] hover:bg-blue-50 transition-colors"
                          data-testid={`dest-edit-${d.dd_coddest}`}
                        >
                          <Pencil className="w-3.5 h-3.5" />
                        </button>
                        <button
                          onClick={() => handleDelete(d.dd_coddest!)}
                          disabled={deleting}
                          title="Elimina"
                          className="w-7 h-7 rounded-lg border flex items-center justify-center text-muted-foreground hover:text-red-600 hover:border-red-300 hover:bg-red-50 transition-colors disabled:opacity-40"
                          data-testid={`dest-delete-${d.dd_coddest}`}
                        >
                          {deleting
                            ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
                            : <Trash2 className="w-3.5 h-3.5" />
                          }
                        </button>
                      </div>
                    </div>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </div>

      {/* Modale add/edit */}
      {modal && (
        <DestModal
          editing={modal === 'add' ? null : modal as DestinationDiv}
          onClose={() => setModal(null)}
          onSaved={() => reload?.()}
        />
      )}
    </div>
  );
}

function AddressRow({ icon, name, line1, city, country, phone }: {
  icon: React.ReactNode; name?: string | null; line1?: string | null;
  city?: string | null; country?: string | null; phone?: string | null;
}) {
  return (
    <div className="flex items-start gap-3">
      <div className="mt-0.5 w-8 h-8 rounded-lg bg-[hsl(210,40%,96%)] flex items-center justify-center text-[hsl(210,64%,29%)]">{icon}</div>
      <div className="text-sm">
        <div className="font-semibold">{name || '—'}</div>
        {line1 && <div className="text-muted-foreground">{line1}</div>}
        <div className="text-muted-foreground">{city || '—'} {country ? `· ${country}` : ''}</div>
        {phone && <div className="text-muted-foreground flex items-center gap-1 mt-0.5"><Phone className="w-3 h-3" /> {phone}</div>}
      </div>
    </div>
  );
}
